<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'v3/components/Form/Input.vue';

const props = defineProps({
  allAgents: {
    type: Array,
    default: () => [],
  },
  agentWeights: {
    type: Object,
    default: () => ({}),
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update-weight']);

const { t } = useI18n();

const totalWeight = computed(() => {
  return Object.values(props.agentWeights).reduce((sum, weight) => sum + (weight || 0), 0);
});

const weightStatus = computed(() => {
  if (totalWeight.value === 0) return { color: 'text-n-slate-11', message: 'No weights set' };
  if (totalWeight.value === 100) return { color: 'text-green-600', message: '✓ Total: 100%' };
  if (totalWeight.value < 100) return { color: 'text-yellow-600', message: `⚠ Total: ${totalWeight.value}% (under 100%)` };
  return { color: 'text-red-600', message: `✗ Total: ${totalWeight.value}% (over 100%)` };
});

const handleWeightChange = (agentId, newWeight) => {
  const weight = parseInt(newWeight) || 0;
  if (weight >= 0 && weight <= 100) {
    emit('update-weight', agentId, weight);
  }
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-end gap-4 w-full justify-between">
      <div class="flex flex-col items-start gap-1 py-1">
        <label class="text-sm font-medium text-n-slate-12 py-1">
          Agent Weight Distribution
        </label>
        <p class="mb-0 text-n-slate-11 text-sm">
          Assign percentage weights to control how conversations are distributed. Set 0% to exclude an agent.
        </p>
      </div>
      <div :class="['text-sm font-medium', weightStatus.color]">
        {{ weightStatus.message }}
      </div>
    </div>

    <!-- All Agents List -->
    <div v-if="allAgents.length > 0" class="flex flex-col gap-2">
      <div
        v-for="agent in allAgents"
        :key="agent.id"
        class="flex items-center gap-3 p-3 border border-n-weak rounded-lg"
      >
        <div class="flex items-center gap-2 flex-1">
          <img
            v-if="agent.avatar_url"
            :src="agent.avatar_url"
            :alt="agent.name"
            class="w-8 h-8 rounded-full"
          />
          <div v-else class="w-8 h-8 rounded-full bg-n-slate-6 flex items-center justify-center text-xs font-medium">
            {{ agent.name.charAt(0).toUpperCase() }}
          </div>
          <div class="flex flex-col">
            <span class="text-sm font-medium text-n-slate-12">
              {{ agent.name }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ agent.email }}
            </span>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <Input
            :model-value="agentWeights[agent.id] || 0"
            type="number"
            min="0"
            max="100"
            class="w-20"
            @update:model-value="handleWeightChange(agent.id, $event)"
          />
          <span class="text-sm text-n-slate-11">%</span>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-else
      class="flex flex-col items-center justify-center p-8 border border-dashed border-n-weak rounded-lg"
    >
      <p class="text-sm text-n-slate-11">
        No agents available in this account.
      </p>
    </div>

    <div class="text-xs text-n-slate-11 bg-n-slate-2 p-3 rounded-lg">
      <strong>Tip:</strong> Weights don't need to total exactly 100%. The system will distribute based on relative weights. 
      For example, 40-30-20-10 works the same as 4-3-2-1.
    </div>
  </div>
</template>
